import { Paperclip, Mail, ArrowLeft, Download, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

const OutlookContent = ({ scenario, onComplete, addFileToDownloads, addNotification }) => {
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emails, setEmails] = useState([]);

  // Initialize emails from scenario or use defaults
  React.useEffect(() => {
    console.log('[OutlookContent] Initializing emails from scenario:', {
      hasScenario: !!scenario,
      hasEmails: !!(scenario?.emails),
      emailsLength: scenario?.emails?.length || 0,
      emails: scenario?.emails,
      scenarioKeys: scenario ? Object.keys(scenario) : []
    });

    if (scenario?.emails && scenario.emails.length > 0) {
      console.log('[OutlookContent] ✅ Using emails from scenario:', scenario.emails.length);
      setEmails(scenario.emails);
    } else {
      console.log('[OutlookContent] ⚠️ No emails in scenario, using defaults');
      // Default emails for realistic inbox
      setEmails([
        {
          id: 1,
          from: 'John Smith <john.smith@company.com>',
          subject: 'Q4 Budget Review Meeting',
          date: '10:30 AM',
          preview: 'Hi team, I wanted to schedule a meeting to discuss the Q4 budget...',
          body: `Hi team,

I wanted to schedule a meeting to discuss the Q4 budget allocations. We need to review the proposed spending for each department and ensure we're staying within our targets.

Please review the attached spreadsheet before the meeting.

Best regards,
John Smith
Senior Financial Analyst`,
          attachments: [
            { name: 'Q4_Budget_2024.xlsx', size: '245 KB' }
          ],
          read: true
        },
        {
          id: 2,
          from: 'IT Department <it-support@company.com>',
          subject: 'Security Update Required',
          date: '9:15 AM',
          preview: 'Action Required: Please update your system security software...',
          body: `Dear User,

This is a reminder to update your system security software to the latest version. The update includes critical security patches that protect against recent vulnerabilities.

To update:
1. Open Windows Security Center
2. Click "Check for Updates"
3. Install all available updates

If you need assistance, please contact IT Support.

Thank you,
IT Department`,
          attachments: [],
          read: true
        },
        {
          id: 3,
          from: 'HR Department <hr@company.com>',
          subject: 'New Company Policy - Remote Work',
          date: 'Yesterday',
          preview: 'We are pleased to announce our new remote work policy...',
          body: `Dear Employees,

We are pleased to announce our new remote work policy effective next month. This policy provides more flexibility for employees to work from home.

Key points:
- Employees can work remotely up to 3 days per week
- Core hours: 10 AM - 3 PM (must be available)
- Monthly in-office meetings are mandatory

Please review the attached policy document for full details.

Best regards,
HR Department`,
          attachments: [
            { name: 'Remote_Work_Policy_2024.pdf', size: '1.2 MB' }
          ],
          read: false
        },
        {
          id: 4,
          from: 'Sarah Johnson <sarah.johnson@vendor.com>',
          subject: 'Invoice #2024-1156',
          date: 'Yesterday',
          preview: 'Please find attached the invoice for services rendered in November...',
          body: `Hi,

Please find attached the invoice for services rendered in November 2024.

Invoice Details:
- Invoice Number: 2024-1156
- Amount Due: $5,430.00
- Due Date: December 15, 2024

Payment can be made via bank transfer to the account details listed on the invoice.

Thank you,
Sarah Johnson
Accounts Receivable`,
          attachments: [
            { name: 'Invoice_2024-1156.pdf', size: '156 KB' }
          ],
          read: false
        },
        {
          id: 5,
          from: 'Marketing Team <marketing@company.com>',
          subject: 'Product Launch Campaign Assets',
          date: '2 days ago',
          preview: 'The final assets for our Q1 product launch campaign are ready...',
          body: `Team,

The final assets for our Q1 product launch campaign are ready for review. Please check the attached files and provide feedback by end of week.

Included:
- Logo variations
- Social media graphics
- Email templates
- Banner ads

Looking forward to your input!

Marketing Team`,
          attachments: [
            { name: 'Campaign_Assets.zip', size: '12.4 MB' },
            { name: 'Brand_Guidelines.pdf', size: '892 KB' }
          ],
          read: false
        }
      ]);
    }
  }, [scenario]);

  const handleAttachmentClick = (attachment) => {
    console.log(`[Outlook] Downloading attachment:`, attachment);

    if (addFileToDownloads) {
      addFileToDownloads(attachment.name, attachment.size, 'file', attachment);

      if (addNotification) {
        addNotification(
          'download',
          'Download Complete',
          `${attachment.name} has been saved to Downloads folder`
        );
      }

      if (onComplete) {
        onComplete('attachment_downloaded', { fileName: attachment.name });
      }
    } else {
      alert(`Downloaded ${attachment.name} to Downloads folder`);
    }
  };

  const handleDeleteEmail = (emailId) => {
    setEmails(prev => prev.filter(email => email.id !== emailId));

    if (onComplete) {
      onComplete('email_deleted', { emailId });
    }

    if (addNotification) {
      addNotification(
        'info',
        'Email Deleted',
        'The email has been moved to trash'
      );
    }

    // If we're viewing the deleted email, go back to inbox
    if (selectedEmail && selectedEmail.id === emailId) {
      setSelectedEmail(null);
    }
  };

  if (selectedEmail) {
    return (
      <div className="h-full bg-white flex flex-col">
        {/* Email Header */}
        <div className="border-b border-gray-200 p-4">
          <button
            onClick={() => setSelectedEmail(null)}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-3"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back to Inbox
          </button>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{selectedEmail.subject}</h2>
          <div className="text-sm text-gray-600">
            <div className="mb-1">
              <span className="font-medium text-gray-700">From:</span> {selectedEmail.from}
            </div>
            <div>
              <span className="font-medium text-gray-700">Date:</span> {selectedEmail.date}
            </div>
          </div>
        </div>

        {/* Email Body */}
        <div className="flex-1 overflow-auto p-6">
          <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
            {selectedEmail.body}
          </div>

          {/* Attachments */}
          {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <Paperclip size={16} className="mr-2" />
                Attachments ({selectedEmail.attachments.length})
              </h3>
              <div className="space-y-2">
                {selectedEmail.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    onClick={() => handleAttachmentClick(attachment)}
                    className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center">
                      <Paperclip size={16} className="mr-2 text-gray-500" />
                      <span className="text-sm font-medium text-gray-900">{attachment.name}</span>
                      <span className="text-xs text-gray-500 ml-2">({attachment.size})</span>
                    </div>
                    <Download size={16} className="text-blue-600" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Email Actions */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-3">
            <button
              onClick={() => handleDeleteEmail(selectedEmail.id)}
              className="flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
            >
              <Trash2 size={16} className="mr-2" />
              Delete Email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 bg-blue-600 text-white">
        <div className="flex items-center">
          <Mail size={24} className="mr-3" />
          <div>
            <h1 className="text-xl font-semibold">Inbox</h1>
            <p className="text-sm text-blue-100">{emails.filter(e => !e.read).length} unread messages</p>
          </div>
        </div>
      </div>

      {/* Email List */}
      <div className="flex-1 overflow-auto">
        {emails.map((email) => (
          <div
            key={email.id}
            onClick={() => setSelectedEmail(email)}
            className={`border-b border-gray-200 p-4 cursor-pointer hover:bg-gray-50 transition-colors ${!email.read ? 'bg-blue-50/50' : ''
              }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center mb-1">
                  {!email.read && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-2 flex-shrink-0"></div>
                  )}
                  <h3 className={`text-sm ${!email.read ? 'font-semibold' : 'font-medium'} text-gray-700 truncate`}>
                    {email.from}
                  </h3>
                </div>
                <div className="flex items-center mb-1">
                  <h4 className={`text-sm ${!email.read ? 'font-semibold text-gray-900' : 'text-gray-800'} truncate flex-1`}>
                    {email.subject}
                  </h4>
                  {email.attachments && email.attachments.length > 0 && (
                    <Paperclip size={14} className="ml-2 text-gray-400 flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-gray-600 truncate">{email.preview}</p>
              </div>
              <div className="text-xs text-gray-500 ml-4 flex-shrink-0">
                {email.date}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OutlookContent;